import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ModalShell } from '../../../components/ui/ModalShell';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';
import { api, ApiError } from '../../../lib/api';
import type { SystemPerson } from '../sap/SystemCreationRow';
import { PlanonPersonForm } from './PlanonPersonForm';
import {
  usePlanonReference,
  emptyDraft,
  draftFromCopy,
  toSelections,
  FIELD_LABEL_CLS,
  type PlanonDraft,
} from './usePlanonReference';

/**
 * Create a person in Planon. Optionally copy every field from an existing
 * CAFM employee, tweak what differs, review the resolved payload, then create.
 * With no copy-from selected it is a blank manual create.
 */
export function PlanonCreateModal({
  person,
  onClose,
  onCreated,
}: {
  person: SystemPerson;
  onClose: () => void;
  onCreated: () => void;
}) {
  const roster = usePlanonReference('roster');
  const [draft, setDraft] = useState<PlanonDraft>(emptyDraft);
  const [copyFrom, setCopyFrom] = useState<number | null>(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [phase, setPhase] = useState<'form' | 'review'>('form');
  const [values, setValues] = useState<Record<string, unknown> | null>(null);

  async function applyCopyFrom(syscode: number | null) {
    setCopyFrom(syscode);
    if (syscode == null) {
      setDraft(emptyDraft());
      return;
    }
    const entry = (roster.data?.entries ?? []).find((e) => e.syscode === syscode);
    const position = entry && typeof entry.payload?.position_syscode === 'number'
      ? (entry.payload.position_syscode as number)
      : null;
    setCopyLoading(true);
    try {
      const r = await api<{ record: Record<string, unknown> | null }>(
        `/api/admin/planon/person-detail?syscode=${syscode}`,
      );
      if (r.record) setDraft(draftFromCopy(r.record, position));
      else toast.error('That person has no Planon detail record');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'could not load the copy-from person');
    } finally {
      setCopyLoading(false);
    }
  }

  const preview = useMutation({
    mutationFn: () =>
      api<{ values: Record<string, unknown> }>(
        `/api/admin/planon/people/${person.id}/planon/preview`,
        { method: 'POST', body: JSON.stringify(toSelections(draft)) },
      ),
    onSuccess: (r) => {
      setValues(r.values);
      setPhase('review');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'could not build the payload'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<{ syscode: number }>(`/api/admin/planon/people/${person.id}/planon/create`, {
        method: 'POST',
        body: JSON.stringify(toSelections(draft)),
      }),
    onSuccess: (r) => {
      toast.success(`${person.full_name} created in Planon (syscode ${r.syscode})`);
      onCreated();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Planon create failed'),
  });

  return (
    <ModalShell
      open
      onClose={onClose}
      titleId="planon-create-title"
      maxWidth="max-w-3xl"
      busy={create.isPending}
    >
      <div className="flex max-h-[85vh] flex-col">
        <div className="border-b border-line/10 px-5 py-3">
          <h2 id="planon-create-title" className="text-sm font-semibold">
            Create {person.full_name} in Planon
          </h2>
          <p className="mt-0.5 text-xs text-fg-dim">
            Code {person.employee_id ?? '—'} · starts {person.start_date ?? '—'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {phase === 'form' ? (
            <div className="space-y-5">
              <label className="block">
                <span className={FIELD_LABEL_CLS}>Copy from an existing CAFM person (optional)</span>
                <div className="mt-1">
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Blank (manual entry)' },
                      ...(roster.data?.entries ?? []).map((e) => ({
                        value: String(e.syscode),
                        label: e.label,
                      })),
                    ]}
                    value={copyFrom != null ? String(copyFrom) : ''}
                    onChange={(v) => void applyCopyFrom(v ? Number(v) : null)}
                    placeholder={roster.isLoading ? 'Loading roster…' : 'Blank (manual entry)'}
                    searchPlaceholder="Search people…"
                    ariaLabel="Copy from an existing CAFM person"
                  />
                </div>
                {copyLoading ? (
                  <span className="mt-1 block text-xs text-fg-dim">Loading their details…</span>
                ) : null}
              </label>
              <PlanonPersonForm draft={draft} onChange={setDraft} />
            </div>
          ) : (
            <ReviewTable values={values ?? {}} />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line/10 px-5 py-3">
          {phase === 'form' ? (
            <>
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={preview.isPending}
                onClick={() => preview.mutate()}
              >
                Review payload
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<ArrowLeft className="h-3.5 w-3.5" />}
                onClick={() => setPhase('form')}
              >
                Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Send className="h-3.5 w-3.5" />}
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                Create in Planon
              </Button>
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

/** The exact values that will be POSTed, for a last human check. */
function ReviewTable({ values }: { values: Record<string, unknown> }) {
  const rows = Object.entries(values);
  return (
    <div>
      <p className="mb-3 text-sm text-fg-muted">
        This is the exact record that will be created in Planon. There is no dry run, so review it
        before creating.
      </p>
      <div className="overflow-hidden rounded-token-sm border border-line/10">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-divider/10">
            {rows.map(([key, value]) => (
              <tr key={key}>
                <td className="w-1/2 bg-surface-2 px-3 py-1.5 font-mono text-xs text-fg-muted">{key}</td>
                <td className="px-3 py-1.5 tabular-nums">{formatValue(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string' && v.includes(' ')) return `"${v}"`; // show PersonType spaces
  return String(v);
}
